import { useMutation } from '@tanstack/react-query';
import { useWallet } from '@txnlab/use-wallet';
import algosdk from 'algosdk';
import axios from 'axios';
import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { algodClient, encodeNote } from '../utils/algo';
import { waitForConfirmation } from '../utils/txn-client';
import getMetadata from '../utils/getMetadata';
import useAssetMetadataQuery from './useAssetMetadataQuery';
import useTraitShuffleStore from './useTraitShuffleStore';

export const useTraitShuffleInitiateMutation = () => {
  const { signTransactions, sendTransactions } = useWallet();
  const { setInitiateShuffleSuccess, setInitiateShuffleUpdates } = useTraitShuffleStore();

  return useMutation({
    mutationFn: async ({ address, selectedNft, selectedTrait, costAdjustment }) => {
      const suggestedParams = await algodClient.getTransactionParams().do();

      const transaction = algosdk.makeAssetTransferTxnWithSuggestedParamsFromObject({
        suggestedParams: suggestedParams,
        from: address,
        to: address,
        assetIndex: parseInt(process.env.NEXT_PUBLIC_TREAT_TOKEN_ID),
        amount: 0,
        note: encodeNote({
          address: address,
          selectedNft,
          selectedTrait,
          costAdjustment,
        }),
      });

      const encodedTransaction = algosdk.encodeUnsignedTransaction(transaction);
      const signedTransactions = await signTransactions([encodedTransaction]);
      const { id } = await sendTransactions(signedTransactions);

      const res = await fetch('/api/trait-shuffle/initiate', {
        method: 'POST',
        headers: {
          Authorization: process.env.NEXT_PUBLIC_API_KEY,
        },
        body: JSON.stringify({
          transactionId: id,
          address,
          selectedNft,
          selectedTrait,
          costAdjustment,
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error?.message || 'Failed to initiate trait shuffle');
      }

      return res.json();
    },
    onSuccess: (data) => {
      setInitiateShuffleUpdates(data.updates);
      setInitiateShuffleSuccess(true);
    },
    onError: (error) => {
      toast.error(error?.message || 'Failed to initiate trait shuffle');
      setInitiateShuffleSuccess(false);
    },
  });
};

export const useTraitShuffleFinalizeMutation = () => {
  const { signTransactions } = useWallet();
  const { reset, initiateShuffleUpdates, selectedNft, setShowSuccessModal, setNewKittyMetadata } = useTraitShuffleStore();

  return useMutation({
    mutationFn: async ({ address, selectedNft, selectedTrait, costAdjustment }) => {
      if (!initiateShuffleUpdates) {
        throw new Error('Trait shuffle not initiated');
      }

      const res = await fetch('/api/trait-shuffle/finalize', {
        method: 'POST',
        headers: {
          Authorization: process.env.NEXT_PUBLIC_API_KEY,
        },
        body: JSON.stringify({ address, selectedNft, selectedTrait, costAdjustment, initiateSwapUpdates: initiateShuffleUpdates }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error?.message || 'An error occurred');
      }

      const txns = await res.json();

      const { u, a } = txns;
      const decodedU = u.map((txn) => Uint8Array.from(Buffer.from(txn, 'base64')));
      const decodedA = a.map((txn) => Uint8Array.from(Buffer.from(txn, 'base64')));

      const signedTransactions = await signTransactions([...decodedU, ...decodedA]);

      const toSend = signedTransactions.map((txn) => {
        if (txn instanceof Uint8Array) {
          return txn;
        } else {
          return Uint8Array.from(txn);
        }
      });

      const sentTransaction = await algodClient.sendRawTransaction(toSend).do();

      await waitForConfirmation(sentTransaction.txId);

      try {
        await axios.post(
          '/api/trait-shuffle/record',
          {
            transactionId: sentTransaction.txId,
            selectedNft,
            selectedTrait,
            costAdjustment,
            address,
            updates: initiateShuffleUpdates,
          },
          {
            headers: {
              Authorization: process.env.NEXT_PUBLIC_API_KEY,
            },
          }
        );
      } catch (error) {
        console.error(error);
      }

      return sentTransaction;
    },
    onSuccess: async (data) => {
      toast.success('Trait shuffle successful');

      try {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        const newMetadata = await getMetadata(selectedNft.id);
        if (newMetadata) {
          setNewKittyMetadata(newMetadata);
          setShowSuccessModal(true);
        } else {
          reset();
          location.reload();
        }
      } catch (error) {
        console.error('Failed to fetch updated metadata:', error);
        reset();
        location.reload();
      }
    },
    onError: (error) => {
      console.log(error);
      toast.error(error?.message || 'Trait shuffle failed');
    },
  });
};

export const useTraitShuffleResolver = () => {
  const { selectedNft, updateMetadata } = useTraitShuffleStore();
  const metadataOneQuery = useAssetMetadataQuery(selectedNft.id);
  const metadataOne = metadataOneQuery.data;

  useEffect(() => {
    if (!metadataOne) return;
    updateMetadata(metadataOne);
  }, [metadataOne]);
};
